<script setup lang="ts">
import axios from 'axios';
import { computed, nextTick, onUnmounted, ref, watch, watchEffect } from 'vue';
import ToolPageHead from '@/components/seo/ToolPageHead.vue';
import DropZone from '@/components/Tools/DropZone.vue';
import FileCard from '@/components/Tools/FileCard.vue';
import PdfLivePreview from '@/components/Tools/PdfLivePreview.vue';
import StatusMessage from '@/components/Tools/StatusMessage.vue';
import ToolHowToCard from '@/components/Tools/ToolHowToCard.vue';
import ToolLayout from '@/components/Tools/ToolLayout.vue';
import ToolSliderField from '@/components/Tools/ToolSliderField.vue';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { usePdfPage } from '@/composables/usePdfPage';
import { usePdfUploadLimit } from '@/composables/usePdfUploadLimit';
import { useToolPageSeoFromInertia } from '@/composables/useToolPageSeo';
import { useTranslations } from '@/composables/useTranslations';
import { dashboard } from '@/routes';
import { addPageNumbers as addPageNumbersPage } from '@/routes/tools/pdf';
import addPageNumbersTool from '@/routes/tools/pdf/add-page-numbers';

const { lineWithMax } = usePdfUploadLimit();
const { t, locale } = useTranslations();

const seo = useToolPageSeoFromInertia({
    seoPrefix: 'tools.pdf.add_page_numbers.seo',
    fallbackTitleKey: 'tools.pdf.add_page_numbers.title',
    fallbackTitleLabel: 'Add page numbers',
    t,
    homeRelativeUrl: dashboard.url(),
    selfRelativeUrl: addPageNumbersPage.url(),
});

function formatFontPt(n: number): string {
    return `${n} pt`;
}

defineOptions({
    layout: {
        breadcrumbs: [
            {
                translationKey: 'tools.nav.home',
                title: 'Home',
                href: dashboard.url(),
            },
            {
                translationKey: 'tools.pdf.add_page_numbers.title',
                title: 'Add page numbers',
                href: addPageNumbersPage.url(),
            },
        ],
    },
});

type Position =
    | 'top-left'
    | 'top-center'
    | 'top-right'
    | 'middle-left'
    | 'middle-center'
    | 'middle-right'
    | 'bottom-left'
    | 'bottom-center'
    | 'bottom-right';

type Format = 'number' | 'page-number' | 'page-of-total' | 'custom';
type Margin = 'small' | 'recommended' | 'big';
type FontFamily = 'helvetica' | 'times' | 'courier';
type Status = 'idle' | 'loading' | 'success' | 'error';

const file = ref<File | null>(null);
const position = ref<Position>('bottom-center');
const margin = ref<Margin>('recommended');
const format = ref<Format>('number');
const customText = ref('Page {n} of {p}');
const startNumber = ref(1);
const fontFamily = ref<FontFamily>('helvetica');
const fontSize = ref(12);
const bold = ref(false);
const italic = ref(false);
const underline = ref(false);
const textColor = ref('#222222');
const skipFirst = ref(false);
const status = ref<Status>('idle');
const statusMessage = ref('');
const previewPage = ref(1);

const pdfCanvas = ref<HTMLCanvasElement | null>(null);
const overlayCanvas = ref<HTMLCanvasElement | null>(null);
const {
    isLoading: previewLoading,
    error: previewError,
    pageCount,
    pageWidthPt,
    previewZoomPct,
    loadFile,
    renderPage,
    unload,
} = usePdfPage();
const totalPreviewPages = computed(() => Math.max(1, pageCount.value));

const marginOptions = computed(() => [
    {
        label: t('tools.pdf.ui.pn_margin_small', 'Small'),
        value: 'small' as Margin,
        hint: t('tools.pdf.ui.pn_margin_hint_small', 'Tighter to the edge'),
    },
    {
        label: t('tools.pdf.ui.pn_margin_recommended', 'Recommended'),
        value: 'recommended' as Margin,
        hint: t('tools.pdf.ui.pn_margin_hint_recommended', 'Balanced default'),
    },
    {
        label: t('tools.pdf.ui.pn_margin_big', 'Big'),
        value: 'big' as Margin,
        hint: t('tools.pdf.ui.pn_margin_hint_big', 'Extra breathing room'),
    },
]);

const formatOptions = computed(() => [
    {
        label: t('tools.pdf.ui.pn_format_number', 'Number'),
        value: 'number' as Format,
        example: t('tools.pdf.ui.pn_format_ex_number', '1, 2, 3'),
    },
    {
        label: t('tools.pdf.ui.pn_format_page_n', 'Page N'),
        value: 'page-number' as Format,
        example: t('tools.pdf.ui.pn_format_ex_page_n', 'Page 1'),
    },
    {
        label: t('tools.pdf.ui.pn_format_n_total', 'N / Total'),
        value: 'page-of-total' as Format,
        example: t('tools.pdf.ui.pn_format_ex_n_total', '1 / 10'),
    },
    {
        label: t('tools.pdf.ui.pn_format_custom', 'Custom text'),
        value: 'custom' as Format,
        example: t('tools.pdf.ui.pn_format_ex_custom', 'Use placeholders'),
    },
]);

const colorPresets = computed(() => [
    { label: t('tools.pdf.ui.color_black', 'Black'), hex: '#222222' },
    { label: t('tools.pdf.ui.color_gray', 'Gray'), hex: '#64748b' },
    { label: t('tools.pdf.ui.color_blue', 'Blue'), hex: '#2563eb' },
    { label: t('tools.pdf.ui.color_white', 'White'), hex: '#ffffff' },
]);

const customSamples = computed(() => [
    {
        label: '{n}',
        insert: '{n}',
    },
    {
        label: t('tools.pdf.ui.pn_format_ex_page_n', 'Page 1'),
        insert: 'Page {n}',
    },
    {
        label: t('tools.pdf.ui.pn_sample_page_of_total', 'Page {n} of {p}'),
        insert: 'Page {n} of {p}',
    },
]);

/** Same compact 3×3 control as Add Watermark → Placement */
const gridPositions: Position[] = [
    'top-left',
    'top-center',
    'top-right',
    'middle-left',
    'middle-center',
    'middle-right',
    'bottom-left',
    'bottom-center',
    'bottom-right',
];

function placementTitle(pos: Position): string {
    const k = `tools.pdf.ui.placement_${pos.replace(/-/g, '_')}`;

    return t(k, pos.replace(/-/g, ' '));
}

/** Same ratio as backend (mm vs ~A4 width) for proportional preview */
function marginFrac(): number {
    const m = margin.value;

    if (m === 'small') {
        return 5 / 210;
    }

    if (m === 'big') {
        return 18 / 210;
    }

    return 10 / 210;
}

function fontCssStack(f: FontFamily): string {
    switch (f) {
        case 'times':
            return 'Georgia, "Times New Roman", Times, serif';
        case 'courier':
            return '"Courier New", Courier, monospace';
        default:
            return 'Helvetica, Arial, sans-serif';
    }
}

function buildPreviewLabel(displayN: number, totalP: number): string {
    switch (format.value) {
        case 'page-number':
            return t('tools.pdf.ui.pn_preview_page_word', 'Page {n}').replace(
                '{n}',
                String(displayN),
            );
        case 'page-of-total':
            return `${displayN} / ${totalP}`;
        case 'custom': {
            return customText.value
                .replaceAll('{n}', String(displayN))
                .replaceAll('{p}', String(totalP));
        }
        default:
            return String(displayN);
    }
}

function insertSample(text: string): void {
    customText.value = text;
}

async function onFileSelected(files: File[]): Promise<void> {
    file.value = files[0] ?? null;
    status.value = 'idle';

    if (!file.value) {
        unload();

        return;
    }

    await loadFile(file.value);
    await nextTick();

    if (!pdfCanvas.value) {
        return;
    }

    previewPage.value = 1;
    await renderPreviewPage(1);
}

function removeFile(): void {
    file.value = null;
    status.value = 'idle';
    previewPage.value = 1;
    unload();
}

async function renderPreviewPage(page: number): Promise<void> {
    if (!pdfCanvas.value) {
        return;
    }

    const clamped = Math.min(totalPreviewPages.value, Math.max(1, page));
    previewPage.value = clamped;
    await renderPage(pdfCanvas.value, clamped, 440);
    await nextTick();
    drawPageNumberOverlay();
}

watch(previewZoomPct, () => {
    if (!file.value || !pdfCanvas.value) {
        return;
    }

    void renderPreviewPage(previewPage.value);
});

function drawPageNumberOverlay(): void {
    if (!pdfCanvas.value || !overlayCanvas.value || !file.value) {
        return;
    }

    const pw = pdfCanvas.value.width;
    const ph = pdfCanvas.value.height;

    if (pw === 0 || ph === 0) {
        return;
    }

    overlayCanvas.value.width = pw;
    overlayCanvas.value.height = ph;

    const ctx = overlayCanvas.value.getContext('2d');

    if (!ctx) {
        return;
    }

    ctx.clearRect(0, 0, pw, ph);

    const pxPerPt = pageWidthPt.value > 0 ? pw / pageWidthPt.value : 1;
    const canvasFontSize = Math.max(6, fontSize.value * pxPerPt);
    const fontWeight = bold.value ? '700' : '400';
    const fontStyleItalic = italic.value ? 'italic' : 'normal';
    ctx.font = `${fontStyleItalic} ${fontWeight} ${canvasFontSize}px ${fontCssStack(fontFamily.value)}`;
    ctx.fillStyle = textColor.value;
    ctx.strokeStyle = textColor.value;

    const total = Math.max(pageCount.value, 1);

    if (skipFirst.value && previewPage.value === 1) {
        return;
    }

    const numberedIndex = skipFirst.value
        ? previewPage.value - 1
        : previewPage.value;
    const previewN = startNumber.value + Math.max(0, numberedIndex - 1);
    const label = buildPreviewLabel(previewN, total);

    const m = pw * marginFrac();
    const bottomBaseline = ph - m - canvasFontSize * 0.15;
    const topBaseline = m + canvasFontSize * 0.85;
    const midBaseline = ph / 2 + canvasFontSize * 0.35;

    let x = m;
    let y = bottomBaseline;
    ctx.textBaseline = 'alphabetic';

    const pos = position.value;

    if (pos.startsWith('top-')) {
        y = topBaseline;
    } else if (pos.startsWith('middle-')) {
        y = midBaseline;
    }

    if (pos.endsWith('-center')) {
        ctx.textAlign = 'center';
        x = pw / 2;
    } else if (pos.endsWith('-right')) {
        ctx.textAlign = 'right';
        x = pw - m;
    } else {
        ctx.textAlign = 'left';
        x = m;
    }

    ctx.fillText(label, x, y);

    if (underline.value) {
        const w = ctx.measureText(label).width;
        let x0 = x;

        if (ctx.textAlign === 'center') {
            x0 = x - w / 2;
        } else if (ctx.textAlign === 'right') {
            x0 = x - w;
        }

        const uy = y + canvasFontSize * 0.12;
        ctx.lineWidth = Math.max(1, canvasFontSize * 0.06);
        ctx.beginPath();
        ctx.moveTo(x0, uy);
        ctx.lineTo(x0 + w, uy);
        ctx.stroke();
    }
}

watchEffect(() => {
    void locale.value;
    void position.value;
    void margin.value;
    void format.value;
    void customText.value;
    void startNumber.value;
    void fontFamily.value;
    void fontSize.value;
    void bold.value;
    void italic.value;
    void underline.value;
    void textColor.value;
    void previewPage.value;
    void pageCount.value;
    void pageWidthPt.value;
    void pdfCanvas.value;
    void overlayCanvas.value;
    drawPageNumberOverlay();
});

onUnmounted(unload);

async function extractError(err: unknown): Promise<string> {
    const e = err as { response?: { data: Blob | object } };

    if (e.response?.data instanceof Blob) {
        try {
            const parsed = JSON.parse(await (e.response.data as Blob).text());
            const msgs = Object.values(parsed.errors ?? {}).flat();

            return (
                (msgs[0] as string) ||
                t(
                    'tools.pdf.ui.page_numbers_failed',
                    'Failed to add page numbers.',
                )
            );
        } catch {
            return t(
                'tools.pdf.ui.page_numbers_failed',
                'Failed to add page numbers.',
            );
        }
    }

    return t('tools.pdf.ui.page_numbers_failed', 'Failed to add page numbers.');
}

async function convert(): Promise<void> {
    if (!file.value || status.value === 'loading') {
        return;
    }

    if (format.value === 'custom' && !customText.value.trim()) {
        status.value = 'error';
        statusMessage.value = t(
            'tools.pdf.ui.page_numbers_custom_required',
            'Please enter custom text using {n} and/or {p}.',
        );

        return;
    }

    status.value = 'loading';
    statusMessage.value = t(
        'tools.pdf.ui.page_numbers_adding',
        'Adding page numbers...',
    );

    const formData = new FormData();
    formData.append('file', file.value);
    formData.append('position', position.value);
    formData.append('margin', margin.value);
    formData.append('format', format.value);

    if (format.value === 'custom') {
        formData.append('custom_text', customText.value.trim());
    }

    formData.append('start_number', String(startNumber.value));
    formData.append('font_family', fontFamily.value);
    formData.append('font_size', String(fontSize.value));
    formData.append('bold', bold.value ? '1' : '0');
    formData.append('italic', italic.value ? '1' : '0');
    formData.append('underline', underline.value ? '1' : '0');
    formData.append(
        'text_color',
        textColor.value.startsWith('#')
            ? textColor.value
            : `#${textColor.value}`,
    );
    formData.append('skip_first', skipFirst.value ? '1' : '0');

    try {
        const response = await axios.post(
            addPageNumbersTool.submit.url(),
            formData,
            { responseType: 'blob' },
        );

        const cd = (response.headers['content-disposition'] as string) ?? '';
        const match = cd.match(/filename="?([^"]+)"?/);
        const filename = match?.[1] ?? 'numbered.pdf';

        const blobUrl = URL.createObjectURL(response.data as Blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = filename;
        a.click();
        URL.revokeObjectURL(blobUrl);

        status.value = 'success';
        statusMessage.value = t(
            'tools.pdf.ui.page_numbers_success',
            'Page numbers added successfully!',
        );
        file.value = null;
        unload();
    } catch (err: unknown) {
        status.value = 'error';
        statusMessage.value = await extractError(err);
    }
}
</script>

<template>
    <ToolPageHead
        v-bind="seo"
        json-ld-head-key="ld-json-pdf-add-page-numbers"
    />

    <ToolLayout
        :title="t('tools.pdf.add_page_numbers.title', 'Add page numbers')"
        :subtitle="t('tools.pdf.add_page_numbers.description', '')"
        :show-how-to="false"
    >
        <div class="grid gap-4 lg:grid-cols-[2fr_3fr]">
            <!-- ── Left: preview ── -->
            <div class="lg:sticky lg:top-4 lg:self-start">
                <PdfLivePreview
                    v-model:zoom="previewZoomPct"
                    :has-file="!!file"
                    :loading="previewLoading"
                    :error="previewError"
                    :current-page="previewPage"
                    :total-pages="pageCount"
                    :rendering-message="
                        t('tools.pdf.ui.rendering', 'Rendering…')
                    "
                    @change-page="renderPreviewPage"
                >
                    <canvas
                        ref="pdfCanvas"
                        class="relative z-0 block max-h-none w-full bg-white dark:bg-gray-950"
                    />
                    <canvas
                        ref="overlayCanvas"
                        class="absolute inset-0 z-10 h-full w-full"
                        style="pointer-events: none"
                    />
                    <template #footer>
                        <p
                            v-if="file && !previewLoading"
                            class="mt-2 text-xs text-gray-400 dark:text-gray-500"
                        >
                            {{
                                t(
                                    'tools.pdf.ui.pn_preview_footer_intro',
                                    'Preview uses selected page. Placeholders:',
                                )
                            }}&nbsp;<code
                                class="rounded bg-gray-100 px-1 py-0.5 dark:bg-gray-800"
                                >{n}</code
                            >
                            {{
                                t(
                                    'tools.pdf.ui.pn_preview_placeholder_n',
                                    'page number,',
                                )
                            }}&nbsp;<code
                                class="rounded bg-gray-100 px-1 py-0.5 dark:bg-gray-800"
                                >{p}</code
                            >
                            {{
                                t(
                                    'tools.pdf.ui.pn_preview_placeholder_p',
                                    'total pages.',
                                )
                            }}
                        </p>
                    </template>
                </PdfLivePreview>
                <div class="mt-4">
                    <ToolHowToCard />
                </div>
            </div>

            <!-- ── Right: controls ── -->
            <div class="space-y-4">
                <div
                    class="rounded-xl border border-border bg-card p-5 shadow-sm"
                >
                    <DropZone
                        accept="application/pdf"
                        :multiple="false"
                        :label="
                            t(
                                'tools.pdf.ui.drop_pdf',
                                'Click or drag a PDF here',
                            )
                        "
                        :sublabel="
                            lineWithMax(
                                t('tools.pdf.ui.one_pdf_file', 'One PDF file'),
                            )
                        "
                        @update:model-value="onFileSelected"
                    />
                    <div v-if="file" class="mt-3">
                        <FileCard
                            :file="file"
                            :show-drag-handle="false"
                            @remove="removeFile"
                        />
                    </div>
                </div>

                <div
                    class="rounded-xl border border-border bg-card p-5 shadow-sm"
                >
                    <div class="space-y-5">
                        <div>
                            <p
                                class="mb-3 text-xs font-medium text-gray-600 dark:text-gray-400"
                            >
                                {{
                                    t('tools.pdf.ui.wm_placement', 'Placement')
                                }}
                            </p>
                            <div class="flex flex-wrap items-center gap-4">
                                <div class="grid grid-cols-3 gap-1.5">
                                    <button
                                        v-for="p in gridPositions"
                                        :key="p"
                                        type="button"
                                        :title="placementTitle(p)"
                                        :aria-label="
                                            t(
                                                'tools.pdf.ui.pn_position_aria',
                                                'Position {label}',
                                            ).replace(
                                                '{label}',
                                                placementTitle(p),
                                            )
                                        "
                                        class="h-9 w-9 rounded-md border-2 transition-colors"
                                        :class="
                                            position === p
                                                ? 'border-brand-600 bg-brand-500'
                                                : 'border-gray-300 bg-gray-100 hover:border-brand-400 hover:bg-brand-50 dark:border-gray-600 dark:bg-gray-700'
                                        "
                                        @click="position = p"
                                    />
                                </div>
                            </div>
                            <p
                                class="mt-2 text-xs text-gray-400 dark:text-gray-500"
                            >
                                {{ t('tools.pdf.ui.wm_selected', 'Selected:') }}
                                <span
                                    class="font-medium text-gray-600 dark:text-gray-300"
                                >
                                    {{ placementTitle(position) }}
                                </span>
                            </p>
                        </div>

                        <div>
                            <p
                                class="mb-2 text-sm font-medium text-gray-700 dark:text-gray-300"
                            >
                                {{
                                    t(
                                        'tools.pdf.ui.pn_margin_title',
                                        'Margin from edge',
                                    )
                                }}
                            </p>
                            <div class="grid grid-cols-3 gap-2">
                                <button
                                    v-for="opt in marginOptions"
                                    :key="opt.value"
                                    type="button"
                                    class="rounded-lg border px-2 py-2 text-center text-xs font-medium transition-colors"
                                    :class="
                                        margin === opt.value
                                            ? 'border-brand-600 bg-brand-50 text-brand-700 dark:bg-brand-950 dark:text-brand-300'
                                            : 'border-border bg-card text-muted-foreground hover:border-brand-300 dark:text-foreground/85'
                                    "
                                    :title="opt.hint"
                                    @click="margin = opt.value"
                                >
                                    <span class="block font-semibold">{{
                                        opt.label
                                    }}</span>
                                    <span
                                        class="mt-0.5 block text-[10px] font-normal text-gray-400 dark:text-gray-500"
                                        >{{ opt.hint }}</span
                                    >
                                </button>
                            </div>
                        </div>

                        <div>
                            <p
                                class="mb-2 text-sm font-medium text-gray-700 dark:text-gray-300"
                            >
                                {{
                                    t('tools.pdf.ui.pn_format_title', 'Format')
                                }}
                            </p>
                            <div class="grid grid-cols-2 gap-2 sm:grid-cols-4">
                                <button
                                    v-for="opt in formatOptions"
                                    :key="opt.value"
                                    type="button"
                                    class="rounded-lg border px-2 py-3 text-sm font-medium transition-colors"
                                    :class="
                                        format === opt.value
                                            ? 'border-brand-600 bg-brand-50 text-brand-700 dark:bg-brand-950 dark:text-brand-300'
                                            : 'border-border bg-card text-muted-foreground hover:border-brand-300 dark:text-foreground/85'
                                    "
                                    @click="format = opt.value"
                                >
                                    <span class="block">{{ opt.label }}</span>
                                    <span class="text-xs text-gray-400">{{
                                        opt.example
                                    }}</span>
                                </button>
                            </div>
                            <div
                                v-if="format === 'custom'"
                                class="mt-3 space-y-2"
                            >
                                <label
                                    for="customText"
                                    class="block text-xs text-gray-500 dark:text-gray-400"
                                >
                                    {{
                                        t(
                                            'tools.pdf.ui.pn_custom_samples_intro',
                                            'Custom text samples:',
                                        )
                                    }}
                                    <code class="rounded bg-muted px-1"
                                        >{n}</code
                                    >
                                    ,
                                    <code class="rounded bg-muted px-1"
                                        >Page {n}</code
                                    >
                                    ,
                                    <code class="rounded bg-muted px-1"
                                        >Page {n} of {p}</code
                                    >
                                </label>
                                <div class="flex flex-wrap gap-1.5">
                                    <button
                                        v-for="s in customSamples"
                                        :key="s.insert"
                                        type="button"
                                        class="rounded-full border border-gray-200 px-2 py-0.5 text-xs text-gray-600 hover:border-brand-400 hover:text-brand-700 dark:border-gray-600 dark:text-gray-300 dark:hover:border-brand-500"
                                        @click="insertSample(s.insert)"
                                    >
                                        {{ s.label }}
                                    </button>
                                </div>
                                <textarea
                                    id="customText"
                                    v-model.trim="customText"
                                    rows="2"
                                    class="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-brand-500 focus:ring-2 focus:ring-brand-500 focus:outline-none dark:border-border dark:bg-muted dark:text-foreground"
                                    :placeholder="
                                        t(
                                            'tools.pdf.ui.placeholder_custom_page_text',
                                            'e.g. Page {n} of {p}',
                                        )
                                    "
                                />
                            </div>
                        </div>

                        <div class="space-y-3 border-t border-border pt-4">
                            <p
                                class="text-sm font-medium text-gray-700 dark:text-gray-300"
                            >
                                {{
                                    t(
                                        'tools.pdf.ui.pn_text_appearance',
                                        'Text appearance',
                                    )
                                }}
                            </p>
                            <div>
                                <label
                                    for="fontFamily"
                                    class="mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400"
                                    >{{ t('tools.pdf.ui.font', 'Font') }}</label
                                >
                                <select
                                    id="fontFamily"
                                    v-model="fontFamily"
                                    class="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-border dark:bg-muted dark:text-foreground"
                                >
                                    <option value="helvetica">
                                        {{
                                            t(
                                                'tools.pdf.ui.pn_font_helvetica',
                                                'Helvetica / Arial-like',
                                            )
                                        }}
                                    </option>
                                    <option value="times">
                                        {{
                                            t(
                                                'tools.pdf.ui.pn_font_times',
                                                'Times / Georgia-like',
                                            )
                                        }}
                                    </option>
                                    <option value="courier">
                                        {{
                                            t(
                                                'tools.pdf.ui.pn_font_courier',
                                                'Courier / monospace',
                                            )
                                        }}
                                    </option>
                                </select>
                            </div>
                            <div>
                                <ToolSliderField
                                    v-model="fontSize"
                                    :label="
                                        t('tools.pdf.ui.font_size', 'Font size')
                                    "
                                    :min="8"
                                    :max="36"
                                    :format-value="formatFontPt"
                                />
                            </div>
                            <div class="flex flex-wrap gap-4">
                                <label
                                    class="flex cursor-pointer items-center gap-2.5 text-sm text-gray-700 dark:text-gray-300"
                                >
                                    <Checkbox v-model="bold" class="shrink-0" />
                                    {{
                                        t('tools.pdf.ui.pn_style_bold', 'Bold')
                                    }}
                                </label>
                                <label
                                    class="flex cursor-pointer items-center gap-2.5 text-sm text-gray-700 dark:text-gray-300"
                                >
                                    <Checkbox
                                        v-model="italic"
                                        class="shrink-0"
                                    />
                                    {{
                                        t(
                                            'tools.pdf.ui.pn_style_italic',
                                            'Italic',
                                        )
                                    }}
                                </label>
                                <label
                                    class="flex cursor-pointer items-center gap-2.5 text-sm text-gray-700 dark:text-gray-300"
                                >
                                    <Checkbox
                                        v-model="underline"
                                        class="shrink-0"
                                    />
                                    {{
                                        t(
                                            'tools.pdf.ui.pn_style_underline',
                                            'Underline',
                                        )
                                    }}
                                </label>
                            </div>
                            <div>
                                <label
                                    class="mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400"
                                    >{{
                                        t(
                                            'tools.pdf.ui.pn_color_label',
                                            'Color',
                                        )
                                    }}</label
                                >
                                <div class="flex flex-wrap items-center gap-2">
                                    <input
                                        v-model="textColor"
                                        type="color"
                                        class="h-9 w-11 cursor-pointer rounded border border-gray-200 dark:border-gray-600"
                                        :aria-label="
                                            t(
                                                'tools.pdf.ui.text_color_picker_aria',
                                                'Text color picker',
                                            )
                                        "
                                    />
                                    <button
                                        v-for="c in colorPresets"
                                        :key="c.hex"
                                        type="button"
                                        class="h-8 rounded-md border px-2 text-[11px]"
                                        :class="
                                            textColor === c.hex
                                                ? 'border-brand-600 ring-1 ring-brand-500'
                                                : 'border-gray-200 dark:border-gray-600'
                                        "
                                        :title="c.label"
                                        :aria-label="
                                            t(
                                                'tools.pdf.ui.color_preset_aria',
                                                'Color {label}',
                                            ).replace('{label}', c.label)
                                        "
                                        :style="{ backgroundColor: c.hex }"
                                        @click="textColor = c.hex"
                                    />
                                </div>
                            </div>
                        </div>

                        <div
                            class="grid grid-cols-2 gap-4 border-t border-border pt-4"
                        >
                            <div>
                                <label
                                    for="startNumber"
                                    class="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300"
                                >
                                    {{
                                        t(
                                            'tools.pdf.ui.pn_start_at',
                                            'Start at',
                                        )
                                    }}
                                </label>
                                <input
                                    id="startNumber"
                                    v-model.number="startNumber"
                                    type="number"
                                    min="1"
                                    class="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-brand-500 focus:ring-2 focus:ring-brand-500 focus:outline-none dark:border-border dark:bg-muted dark:text-foreground"
                                />
                            </div>
                        </div>

                        <Label
                            for="skipFirst"
                            class="!flex cursor-pointer gap-4 rounded-xl border border-gray-200 bg-gray-50/80 p-4 font-normal shadow-sm transition-colors hover:border-brand-300 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900/50 dark:hover:border-brand-600 dark:hover:bg-gray-900"
                        >
                            <Checkbox
                                id="skipFirst"
                                v-model="skipFirst"
                                class="mt-1 shrink-0"
                            />
                            <div class="min-w-0 flex-1 space-y-1">
                                <span
                                    class="text-sm leading-snug font-semibold text-gray-900 dark:text-gray-100"
                                >
                                    {{
                                        t(
                                            'tools.pdf.ui.pn_skip_first_title',
                                            'Skip first page (cover page)',
                                        )
                                    }}
                                </span>
                                <p
                                    class="text-xs leading-relaxed text-gray-500 dark:text-gray-400"
                                >
                                    {{
                                        t(
                                            'tools.pdf.ui.pn_skip_first_desc',
                                            'Use the opening page without a footer number — counting starts from the next page while keeping page counts correct.',
                                        )
                                    }}
                                </p>
                            </div>
                        </Label>
                    </div>
                </div>

                <Button
                    class="w-full bg-brand-600 text-white hover:bg-brand-700"
                    :disabled="!file || status === 'loading'"
                    @click="convert"
                >
                    {{ t('tools.pdf.ui.pn_button', 'Add Page Numbers') }}
                </Button>

                <StatusMessage :status="status" :message="statusMessage" />
            </div>
        </div>
    </ToolLayout>
</template>
